import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon, SignalIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { platoonApi } from '../services/api';
import { useUnitStore } from '../store/unitStore';
import { useFarmbaseStore } from '../store/farmbaseStore';
import { useAuthStore } from '../store/authStore';
import { useRealtimeStore } from '../store/realtimeStore';
import UnitFilter from '../components/UnitFilter';
import PhaseChip from '../components/PhaseChip';
import PhaseControl from '../components/PhaseControl';
import { PLATOON_PHASES, type Platoon, type PlatoonPhase } from '../types/domain';

export default function PlatoonsPage() {
  const { user } = useAuthStore();
  const { units, getEffectiveUnitIds, isHQ } = useUnitStore();
  const { farmbases, fetchFarmbases, selectedFarmbaseId, setSelectedFarmbaseId } = useFarmbaseStore();
  const { sosEvents } = useRealtimeStore();

  const userIsHQ = isHQ(user?.unitId ?? null);
  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId ?? null);
  const singleUnitId = effectiveUnitIds && effectiveUnitIds.length === 1 ? effectiveUnitIds[0] : null;

  const [search, setSearch] = useState('');
  const [phaseFilter, setPhaseFilter] = useState<PlatoonPhase | 'ALL'>('ALL');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    fetchFarmbases(singleUnitId ?? undefined);
  }, [fetchFarmbases, singleUnitId]);

  // Reset farmbase sub-filter when the unit scope changes.
  useEffect(() => {
    setSelectedFarmbaseId(null);
  }, [singleUnitId, setSelectedFarmbaseId]);

  const { data, isLoading } = useQuery({
    queryKey: ['platoons', singleUnitId, selectedFarmbaseId, phaseFilter],
    queryFn: () =>
      platoonApi.getAll({
        unitId: singleUnitId ?? undefined,
        farmbaseId: selectedFarmbaseId ?? undefined,
        phase: phaseFilter === 'ALL' ? undefined : phaseFilter,
      }),
    refetchInterval: 5000,
  });

  const activeSosPlatoonIds = useMemo(() => {
    const set = new Set<string>();
    sosEvents.forEach((e) => {
      if (['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'].includes(e.status) && e.platoonId) {
        set.add(e.platoonId);
      }
    });
    return set;
  }, [sosEvents]);

  const unitName = (unitId: string) => units.find((u) => u.id === unitId)?.name || 'Unit';

  const platoons: Platoon[] = useMemo(() => {
    let list: Platoon[] = data?.data?.platoons || [];
    // Defence-in-depth: apply scope client-side too (backend already scopes).
    if (effectiveUnitIds) list = list.filter((p) => effectiveUnitIds.includes(p.unitId));
    if (selectedFarmbaseId) list = list.filter((p) => p.farmbaseId === selectedFarmbaseId);
    if (phaseFilter !== 'ALL') list = list.filter((p) => p.currentPhase === phaseFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.callsign?.toLowerCase().includes(q) ||
          (p.commanderName ?? '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => a.callsign.localeCompare(b.callsign));
  }, [data, effectiveUnitIds, selectedFarmbaseId, phaseFilter, search]);

  const scopedFarmbases = singleUnitId ? farmbases.filter((f) => f.unitId === singleUnitId) : [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-display font-bold text-gray-100">Platoons</h1>
          <p className="text-gray-500 text-[11px]">
            {userIsHQ ? 'All tracked platoons across the exercise' : 'Your unit’s platoons'}
          </p>
        </div>
        {userIsHQ && <UnitFilter />}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search callsign / commander"
            className="pl-9 pr-3 py-2 rounded-lg bg-command-bg border border-command-border text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-command-accent w-64"
          />
        </div>

        {/* Farmbase sub-filter (only when a single unit is in scope) */}
        {singleUnitId && scopedFarmbases.length > 0 && (
          <select
            aria-label="Filter by farmbase"
            value={selectedFarmbaseId ?? ''}
            onChange={(e) => setSelectedFarmbaseId(e.target.value || null)}
            className="py-2 px-3 rounded-lg bg-command-bg border border-command-border text-sm text-gray-100 focus:outline-none focus:border-command-accent"
          >
            <option value="">All farmbases</option>
            {scopedFarmbases.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}

        {/* Phase filter chips */}
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => setPhaseFilter('ALL')}
            className={clsx(
              'px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors',
              phaseFilter === 'ALL'
                ? 'bg-command-accent/20 text-command-accent border-command-accent/40'
                : 'text-gray-400 border-command-border hover:border-command-accent/40'
            )}
          >
            All Phases
          </button>
          {PLATOON_PHASES.map((phase) => (
            <button
              key={phase}
              type="button"
              aria-label={`Filter by phase ${phase}`}
              onClick={() => setPhaseFilter(phase)}
              className={clsx(
                'rounded-full transition-transform',
                phaseFilter === phase ? 'ring-2 ring-offset-1 ring-offset-command-bg scale-105' : 'opacity-80 hover:opacity-100'
              )}
            >
              <PhaseChip phase={phase} size="xs" />
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-command-border bg-command-panel overflow-hidden">
        <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-command-border text-gray-400 text-[11px] uppercase tracking-wide">
                <th className="text-left py-2 px-3 font-semibold">Callsign</th>
                <th className="text-left py-2 px-3 font-semibold">Unit</th>
                <th className="text-left py-2 px-3 font-semibold">Farmbase</th>
                <th className="text-left py-2 px-3 font-semibold">Phase</th>
                <th className="text-left py-2 px-3 font-semibold">Commander</th>
                <th className="text-center py-2 px-3 font-semibold">Strength</th>
                <th className="text-center py-2 px-3 font-semibold">Device</th>
              </tr>
            </thead>
            <tbody>
              {platoons.map((p) => {
                const hasSos = p.sos || activeSosPlatoonIds.has(p.id);
                const isExpanded = expandedId === p.id;
                return (
                  <Fragment key={p.id}>
                  <tr
                    onClick={() => setExpandedId(isExpanded ? null : p.id)}
                    className={clsx(
                      'border-b border-command-border/60 hover:bg-command-bg/40 cursor-pointer',
                      hasSos && 'bg-red-500/10',
                      isExpanded && 'bg-command-bg/40'
                    )}
                  >
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2 font-semibold text-gray-100">
                        {p.callsign}
                        {hasSos && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500 text-white animate-pulse">
                            SOS
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-500">Platoon {p.platoonNumber}</div>
                    </td>
                    <td className="py-2 px-3 text-gray-300">{p.unit?.name || unitName(p.unitId)}</td>
                    <td className="py-2 px-3 text-gray-400">{p.farmbase?.name || '—'}</td>
                    <td className="py-2 px-3">
                      <PhaseChip phase={p.currentPhase} />
                    </td>
                    <td className="py-2 px-3 text-gray-300">{p.commanderName || '—'}</td>
                    <td className="py-2 px-3 text-center text-gray-300">{p.strength ?? '—'}</td>
                    <td className="py-2 px-3 text-center">
                      {p.device ? (
                        <span
                          className={clsx(
                            'inline-flex items-center gap-1 text-[11px]',
                            p.device.status === 'ACTIVE' ? 'text-green-400' : 'text-gray-500'
                          )}
                          title={`Battery ${p.device.batteryLevel ?? '?'}% · Signal ${p.device.signalStrength ?? '?'}`}
                        >
                          <SignalIcon className="w-4 h-4" />
                          {p.device.batteryLevel != null ? `${p.device.batteryLevel}%` : '—'}
                        </span>
                      ) : (
                        <span className="text-gray-600 text-[11px]">no device</span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-command-border/60 bg-command-bg/20">
                      <td colSpan={7} className="py-3 px-3">
                        <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 font-tactical">
                          Phase tagging — {p.callsign}
                        </div>
                        <PhaseControl platoon={p} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
              {!isLoading && platoons.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500 text-sm">
                    No platoons match the current filters.
                  </td>
                </tr>
              )}
              {isLoading && platoons.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500 text-sm">
                    Loading platoons…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
